import { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import axios from 'axios'

import { baseURL } from '../../config'

dayjs.extend(relativeTime)

const describeAcquisition = acquisition => {
	const { first = {}, clickIds = {} } = acquisition
	const source = clickIds.oppref
		? 'ChatGPT ad'
		: clickIds.gclid
		? 'Google ad'
		: clickIds.fbclid
		? 'Meta ad'
		: first.source
		? [first.source, first.medium].filter(Boolean).join(' / ')
		: first.referrer || 'Direct'
	return [source, acquisition.method, acquisition.platform].filter(Boolean).join(' · ')
}

function UsersList({ isDarkMode }) {
	const [searchTerm, setSearchTerm] = useState('')
	const [activeSearch, setActiveSearch] = useState('')
	const [users, setUsers] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [loading, setLoading] = useState(false)
	const [teamModal, setTeamModal] = useState(null)
	const [teamLoading, setTeamLoading] = useState(false)
	const [devicesModal, setDevicesModal] = useState(null)
	const [sortBy, setSortBy] = useState('recent')
	const [planFilter, setPlanFilter] = useState('all')
	const [activityFilter, setActivityFilter] = useState('all')
	const [stripeModal, setStripeModal] = useState(null)
	const [stripeLoading, setStripeLoading] = useState(false)
	const [companiesModal, setCompaniesModal] = useState(null)
	const [trialModal, setTrialModal] = useState(null)
	const [trialDays, setTrialDays] = useState(1)

	const highlightText = (text, search) => {
		if (!search || !text) return text
		const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'))
		return parts.map((part, i) =>
			part.toLowerCase() === search.toLowerCase() ? (
				<span key={i} className="bg-yellow-500 text-black px-0.5 rounded">
					{part}
				</span>
			) : (
				part
			)
		)
	}

	const fetchUsers = async (search = '', sort = sortBy, plan = planFilter, activity = activityFilter) => {
		try {
			setLoading(true)
			setActiveSearch(search)
			const userToken = localStorage.getItem('token')
			const params = new URLSearchParams()
			if (search) params.append('search', search)
			if (sort) params.append('sort', sort)
			if (plan) params.append('plan', plan)
			if (activity) params.append('activity', activity)

			const url = `${baseURL}/api/admin/users${params.toString() ? '?' + params.toString() : ''}`

			const res = await axios.get(url, {
				headers: { authorization: userToken },
			})
			setUsers(res.data.users)
			setTotalCount(res.data.totalCount)
			setLoading(false)
		} catch (error) {
			console.log('error', error)
			setLoading(false)
		}
	}

	const updateFreeTrial = async userId => {
		try {
			const userToken = localStorage.getItem('token')
			await axios.post(
				`${baseURL}/api/admin/toggletrial/${userId}`,
				{ days: trialDays },
				{ headers: { authorization: userToken } }
			)
			setTrialModal(null)
			fetchUsers(searchTerm, sortBy, planFilter, activityFilter)
		} catch (err) {
			console.log(err)
			alert('Error extending free trial')
			setTrialModal(null)
		}
	}

	const stopFreeTrial = async userId => {
		try {
			const confirm = window.confirm('Are you sure to stop the free trial?')
			if (!confirm) return

			const userToken = localStorage.getItem('token')
			await axios.post(
				`${baseURL}/api/admin/stoptrial/${userId}`,
				{},
				{ headers: { authorization: userToken } }
			)
			fetchUsers(searchTerm, sortBy, planFilter, activityFilter)
		} catch (err) {
			console.log(err)
			alert('Error stopping free trial')
		}
	}

	const fetchTeamDetails = async teamId => {
		try {
			setTeamLoading(true)
			const userToken = localStorage.getItem('token')
			const res = await axios.get(`${baseURL}/api/admin/team/${teamId}`, {
				headers: { authorization: userToken },
			})
			setTeamModal(res.data)
			setTeamLoading(false)
		} catch (err) {
			console.log(err)
			alert('Error fetching team details')
			setTeamLoading(false)
		}
	}

	const checkStripeStatus = async userId => {
		try {
			setStripeLoading(true)
			const userToken = localStorage.getItem('token')
			const res = await axios.get(`${baseURL}/api/admin/stripe-check/${userId}`, {
				headers: { authorization: userToken },
			})
			setStripeModal({ ...res.data, userId })
			setStripeLoading(false)
		} catch (err) {
			console.log(err)
			alert('Error checking Stripe status')
			setStripeLoading(false)
		}
	}

	const syncStripeStatus = async userId => {
		try {
			const confirm = window.confirm('Sync this user with Stripe data?')
			if (!confirm) return

			const userToken = localStorage.getItem('token')
			const res = await axios.post(
				`${baseURL}/api/admin/sync-stripe/${userId}`,
				{},
				{ headers: { authorization: userToken } }
			)
			alert(res.data.message)
			setStripeModal(null)
			fetchUsers(searchTerm, sortBy, planFilter, activityFilter)
		} catch (err) {
			console.log(err)
			alert('Error syncing with Stripe')
		}
	}

	const clearUserCache = async userId => {
		if (!window.confirm('Clear cache for this user?')) return
		try {
			const userToken = localStorage.getItem('token')
			await axios.post(
				`${baseURL}/api/admin/clear-cache/${userId}`,
				{},
				{ headers: { authorization: userToken } }
			)
			alert('Cache cleared')
		} catch (err) {
			console.log(err)
			alert('Error clearing cache')
		}
	}

	useEffect(() => {
		if (searchTerm === '') {
			fetchUsers()
		}
	}, [searchTerm])

	useEffect(() => {
		fetchUsers()
	}, [])

	const cardStyle = isDarkMode
		? 'bg-gray-800 text-white hover:bg-gray-700'
		: 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-200'

	const modalBg = isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
	const innerBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
	const inputClass = isDarkMode
		? 'bg-gray-800 border border-gray-600 text-white'
		: 'bg-white border border-gray-300 text-gray-900'
	const textHeading = isDarkMode ? 'text-white' : 'text-gray-900'
	const textBody = isDarkMode ? 'text-gray-300' : 'text-gray-600'
	const textLabel = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const textFaint = isDarkMode ? 'text-gray-500' : 'text-gray-400'
	const textGreen = isDarkMode ? 'text-green-400' : 'text-green-600'
	const textYellow = isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
	const textRed = isDarkMode ? 'text-red-400' : 'text-red-600'
	const textBlue = isDarkMode ? 'text-blue-400' : 'text-blue-600'
	const closeBtn = isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'

	function normalizeCompanyName(name) {
		if (!name) return 'Unknown'

		return name
			?.split('_')
			?.map(word => word.toUpperCase())
			?.join(' ')
	}

	const sortDevicesByLastCheckIn = devices => {
		return [...devices].sort(
			(a, b) => new Date(b.lastCheckIn) - new Date(a.lastCheckIn)
		)
	}

	useEffect(() => {
		fetchUsers(searchTerm, sortBy, planFilter, activityFilter)
	}, [sortBy, planFilter, activityFilter])

	return (
		<>
			<div className={`${isDarkMode ? 'bg-dark-color' : 'bg-white'}`}>
				<h1 className={`text-center text-2xl font-bold p-4`}>
					Admin Panel
				</h1>

				<div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-4 px-4">
					<form
						onSubmit={e => {
							e.preventDefault()
							fetchUsers(searchTerm)
						}}
						className="flex items-center"
					>
					<input
						type="text"
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
						className={`p-2 rounded-md ${inputClass}`}
						placeholder="Search users..."
					/>
						<button
							type="submit"
							className="bg-blue-500 text-white px-4 py-2 ml-2 rounded"
						>
							Search
						</button>
					</form>

					<div className="relative">
						<select
							value={planFilter}
							onChange={e => setPlanFilter(e.target.value)}
							className={`appearance-none bg-none ${inputClass} text-sm rounded-lg px-4 py-2 pr-8 cursor-pointer hover:border-gray-400 focus:outline-none focus:border-blue-500`}
						>
							<option value="all">All Plans</option>
							<option value="free">Free</option>
							<option value="trial">Trial</option>
							<option value="pro_plan">Pro</option>
							<option value="plus_plan">Plus</option>
							<option value="team_plan">Team</option>
						</select>
					<div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${textLabel}`}>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4M8 15l4 4 4-4" />
						</svg>
					</div>
					</div>

					<div className="relative">
						<select
							value={activityFilter}
							onChange={e => setActivityFilter(e.target.value)}
							className={`appearance-none bg-none ${inputClass} text-sm rounded-lg px-4 py-2 pr-8 cursor-pointer hover:border-gray-400 focus:outline-none focus:border-blue-500`}
						>
							<option value="all">All Activity</option>
							<option value="active1">Active in 24 hours</option>
							<option value="active7">Active in 7 days</option>
							<option value="active30">Active in 30 days</option>
							<option value="inactive3">Inactive 3+ days</option>
							<option value="inactive7">Inactive 7+ days</option>
							<option value="inactive14">Inactive 14+ days</option>
							<option value="inactive30">Inactive 30+ days</option>
							<option value="inactive45">Inactive 45+ days</option>
						</select>
					<div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${textLabel}`}>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4M8 15l4 4 4-4" />
						</svg>
					</div>
					</div>

					<div className="relative">
						<select
							value={sortBy}
							onChange={e => setSortBy(e.target.value)}
							className={`appearance-none bg-none ${inputClass} text-sm rounded-lg px-4 py-2 pr-8 cursor-pointer hover:border-gray-400 focus:outline-none focus:border-blue-500`}
						>
							<option value="recent">Recently Registered</option>
							<option value="lastLogin">Recently Logged In</option>
							<option value="devices">Most Devices</option>
							<option value="companies">Most Companies</option>
						</select>
					<div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${textLabel}`}>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4M8 15l4 4 4-4" />
						</svg>
					</div>
					</div>

					</div>

			<p className={`text-sm ${textLabel} px-4 mb-2`}>
				{totalCount} users {users?.length < totalCount && `(showing ${users?.length})`}
			</p>

				{loading ? (
					<div className="text-center text-xl font-bold">Loading...</div>
				) : users?.length > 0 ? (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 p-2 md:p-4">
						{users.map(user => (
								<div
									key={user._id}
									className={`${cardStyle} p-2 md:p-4 rounded-md shadow-md transition duration-300 ease-in-out`}
								>
									<div className="flex items-center gap-2 mb-1 md:mb-2">
										<h2 className="text-sm md:text-xl font-bold truncate">
											{highlightText(user.name, activeSearch)}
										</h2>
										{user.blocked && (
											<span className="text-xs bg-red-600 px-2 py-0.5 rounded">
												Blocked
											</span>
										)}
									</div>
									<p className="text-xs md:text-base truncate">
										{highlightText(user.email, activeSearch)}
									</p>
								<p className="text-xs md:text-base">
									Last Login:{' '}
									<span className={`italic ${textBody}`}>
										{dayjs(user.lastLoggedAt).format('hh:mm A MMM DD YYYY')}
									</span>
								</p>

								<p className="text-xs md:text-base">
									Registered:{' '}
									<span className={`italic ${textBody}`}>
										{dayjs(user.createdAt).format('hh:mm A MMM DD YYYY')}
									</span>
								</p>

								{user.acquisition && (
									<p className="text-xs md:text-base">
										Came from:{' '}
										<span className={`italic ${textBody}`}>
											{describeAcquisition(user.acquisition)}
										</span>
									</p>
								)}

								<p className="text-xs md:text-base">
									Trial End:{' '}
									<span className={`italic ${textBody}`}>
										{dayjs(user.trial_end).format('hh:mm A MMM DD YYYY')}
									</span>
								</p>

								<p>
									Subscription:{' '}
									<span
										className={`italic text-md ${
											user.subscription?.paid
												? textGreen
												: textBody
										}`}
									>
										{user.subscription?.type || 'free'}
										{user.subscription?.paid && ' ✓'}
									</span>
								</p>

								{user.teamInfo && (
									<div className={`mt-2 p-2 rounded-md border ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
										<p className={`${isDarkMode ? 'text-blue-300' : 'text-blue-700'} font-semibold`}>
											Team: {user.teamInfo.teamName}
										</p>
										<p className={`text-sm ${textBody}`}>
											Role:{' '}
											<span
												className={
													user.teamInfo.role === 'owner'
														? `${textYellow} font-bold`
														: textBody
												}
											>
												{user.teamInfo.role}
											</span>
										</p>
										<p className={`text-sm ${textBody}`}>
											Members: {user.teamInfo.memberCount} / {user.teamInfo.seats}
										</p>
										<button
											onClick={() => fetchTeamDetails(user.teamInfo.teamId)}
											className="mt-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
										>
											View Team Details
										</button>
									</div>
								)}

								<p className={`text-xs ${textLabel}`}>
									Companies ({user?.companyNames?.length || 0})
								</p>
								{user?.companyNames?.length > 0 && (
									<div className="flex flex-wrap gap-1 mt-1">
										{user.companyNames.slice(0, 3).map((company, index) => (
											<span
												key={index}
												className={`text-xs ${innerBg} px-2 py-0.5 rounded`}
											>
												{highlightText(normalizeCompanyName(company), activeSearch)}
											</span>
										))}
										{user.companyNames.length > 3 && (
											<button
												onClick={() => setCompaniesModal({
													user: user.name,
													companies: user.companyNames
												})}
												className={`text-xs ${textBlue} hover:underline cursor-pointer`}
											>
												+{user.companyNames.length - 3} more
											</button>
										)}
									</div>
								)}

								<div className="mt-1">
									<h3 className="text-lg font-semibold">
										Devices ({user?.devices?.length || 0})
									</h3>
									{user?.devices?.length > 0 ? (
										<>
											<div className="space-y-2">
												{sortDevicesByLastCheckIn(user?.devices)
													?.slice(0, 5)
													?.map((device, index) => (
														<div
															key={index}
															className={`${innerBg} p-2 rounded text-xs`}
														>
															<div className="flex justify-between items-start">
																<div>
																	<div className="flex items-center gap-2 flex-wrap">
																		<span
																			className={`px-1.5 py-0.5 rounded text-[10px] ${
																				device.deviceType === 'extension'
																					? 'bg-purple-600'
																					: 'bg-green-600'
																			}`}
																		>
																			{device.deviceType || 'unknown'}
																		</span>
																		{device.loginMethod && (
																			<span
																				className={`px-1.5 py-0.5 rounded text-[10px] ${
																					device.loginMethod === 'google'
																						? 'bg-orange-600'
																						: isDarkMode ? 'bg-gray-600' : 'bg-gray-400'
																				}`}
																			>
																				{device.loginMethod === 'google' ? 'Google' : 'Email'}
																			</span>
																		)}
																		<span className="font-medium">
																			{normalizeCompanyName(device?.companyName) || 'Unknown'}
																		</span>
																	</div>
																<p className={`${textLabel} mt-1`}>
																	{device.os} - {device.browser}
																	{device.deviceType === 'extension' && device.extensionVersion && (
																		<span className={textFaint}> (v{device.extensionVersion})</span>
																	)}
																</p>
															{(() => {
																const parts = [
																	device.city,
																	device.region,
																	device.country,
																].filter(v => v && v !== 'Unknown' && v !== '')
																return (
																	<p className={`${textFaint} mt-0.5`}>
																		📍 {parts.length ? parts.join(', ') : 'Unknown location'}
																	</p>
																)
															})()}
															<div className={`${textFaint} mt-1 space-y-0.5`}>
																<p>First: {dayjs(device.firstUsed).format('MMM DD, YYYY hh:mm A')}</p>
																<p>Last: {dayjs(device.lastCheckIn).fromNow()}</p>
															</div>
																</div>
															</div>
														</div>
													))}
											</div>
											{user?.devices?.length > 5 && (
												<button
													onClick={() =>
														setDevicesModal({
															user: user.name,
															devices: sortDevicesByLastCheckIn(user?.devices),
														})
													}
													className={`mt-2 text-xs ${textBlue} hover:underline`}
												>
													Show all {user.devices.length} sessions
												</button>
											)}
										</>
									) : (
										<p className={`text-sm ${textLabel} italic`}>
											No devices registered
										</p>
									)}
								</div>

								<div className="flex flex-wrap gap-2 mt-4">
									<button
										onClick={() => {
											setTrialModal(user._id)
											setTrialDays(1)
										}}
										className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm"
									>
										Extend Trial
									</button>
									{user.trial_end && new Date(user.trial_end) > new Date() && (
										<button
											onClick={() => stopFreeTrial(user._id)}
											className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded text-sm"
										>
											Stop Trial
										</button>
									)}
									<button
										onClick={() => checkStripeStatus(user._id)}
										className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm"
									>
										Check Stripe
									</button>
									<button
										onClick={() => clearUserCache(user._id)}
										className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
									>
										Clear Cache
									</button>
								</div>
								</div>
							))}
					</div>
				) : (
					<div className="text-center text-xl font-bold">No users found</div>
				)}
			</div>

		{teamModal && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className={`${modalBg} rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto`}>
					<div className="flex justify-between items-center mb-4">
						<h2 className={`text-xl font-bold ${textHeading}`}>
							{teamModal.team.name}
						</h2>
						<button
							onClick={() => setTeamModal(null)}
							className={`${closeBtn} text-2xl`}
						>
							×
						</button>
					</div>

					<div className="grid grid-cols-4 gap-3 mb-4">
						<div className={`${innerBg} p-3 rounded`}>
							<p className={`${textLabel} text-sm`}>Total Seats</p>
							<p className={`${textHeading} text-xl font-bold`}>
								{teamModal.stats.totalSeats}
							</p>
						</div>
						<div className={`${innerBg} p-3 rounded`}>
							<p className={`${textLabel} text-sm`}>Active</p>
							<p className={`${textGreen} text-xl font-bold`}>
								{teamModal.stats.activeMembers}
							</p>
						</div>
						<div className={`${innerBg} p-3 rounded`}>
							<p className={`${textLabel} text-sm`}>Pending</p>
							<p className={`${textYellow} text-xl font-bold`}>
								{teamModal.stats.pendingInvites}
							</p>
						</div>
						<div className={`${innerBg} p-3 rounded`}>
							<p className={`${textLabel} text-sm`}>Available</p>
							<p className={`${textBlue} text-xl font-bold`}>
								{teamModal.stats.availableSeats}
							</p>
						</div>
					</div>

					<div className="mb-4">
						<p className={`text-sm ${textLabel}`}>
							Status:{' '}
							<span
								className={
									teamModal.team.status === 'active'
										? textGreen
										: textRed
								}
							>
								{teamModal.team.status}
							</span>
						</p>
						<p className={`text-sm ${textLabel}`}>
							Created: {dayjs(teamModal.team.createdAt).format('MMM DD, YYYY')}
						</p>
					</div>

					<h3 className={`text-lg font-semibold ${textHeading} mb-2`}>
						Members ({teamModal.members.length})
					</h3>
					<div className="space-y-2 mb-4">
						{teamModal.members.map(member => (
							<div
								key={member.id}
								className={`${innerBg} p-3 rounded flex justify-between items-center`}
							>
								<div>
									<p className={`${textHeading} font-medium`}>
										{member.name || 'Unknown'}
										{member.role === 'owner' && (
											<span className="ml-2 text-xs bg-yellow-600 px-2 py-0.5 rounded">
												Owner
											</span>
										)}
									</p>
									<p className={`${textLabel} text-sm`}>{member.email}</p>
								</div>
								<div className="text-right">
									<p
										className={`text-sm ${
											member.subscription?.paid
												? textGreen
												: textLabel
										}`}
									>
										{member.subscription?.type || 'free'}
										{member.subscription?.paid && ' ✓'}
									</p>
									<p className={`${textFaint} text-xs`}>
										Devices: {member.deviceCount}
									</p>
									<p className={`${textFaint} text-xs`}>
										Last login: {dayjs(member.lastLoggedAt).fromNow()}
									</p>
								</div>
							</div>
						))}
					</div>

					{teamModal.pendingInvites.length > 0 && (
						<>
							<h3 className={`text-lg font-semibold ${textHeading} mb-2`}>
								Pending Invites ({teamModal.pendingInvites.length})
							</h3>
							<div className="space-y-2">
								{teamModal.pendingInvites.map(invite => (
									<div
										key={invite.id}
										className={`${innerBg} p-3 rounded flex justify-between items-center`}
									>
										<div>
											<p className={isDarkMode ? 'text-yellow-300' : 'text-yellow-600'}>{invite.email}</p>
											<p className={`${textLabel} text-xs`}>
												Invited by: {invite.invitedBy}
											</p>
										</div>
										<p className={`${textFaint} text-xs`}>
											Expires: {dayjs(invite.expiresAt).fromNow()}
										</p>
									</div>
								))}
							</div>
						</>
					)}
				</div>
			</div>
		)}

		{teamLoading && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className={`${textHeading} text-xl`}>Loading team details...</div>
			</div>
		)}

		{devicesModal && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
				<div className={`${modalBg} rounded-lg p-4 md:p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
					<div className="flex justify-between items-center mb-4">
						<h2 className={`text-lg md:text-xl font-bold ${textHeading}`}>
							{devicesModal.user} - All Sessions ({devicesModal.devices.length})
						</h2>
						<button
							onClick={() => setDevicesModal(null)}
							className={`${closeBtn} text-2xl`}
						>
							×
						</button>
					</div>

					<div className="space-y-3">
						{devicesModal.devices.map((device, index) => (
							<div key={index} className={`${innerBg} p-3 rounded`}>
								<div className="flex justify-between items-start">
									<div>
										<div className="flex items-center gap-2 mb-1 flex-wrap">
											<span
												className={`px-2 py-0.5 rounded text-xs ${
													device.deviceType === 'extension'
														? 'bg-purple-600'
														: 'bg-green-600'
												}`}
											>
												{device.deviceType || 'unknown'}
											</span>
											{device.loginMethod && (
												<span
													className={`px-2 py-0.5 rounded text-xs ${
														device.loginMethod === 'google'
															? 'bg-orange-600'
															: isDarkMode ? 'bg-gray-600' : 'bg-gray-400'
													}`}
												>
													{device.loginMethod === 'google' ? 'Google' : 'Email'}
												</span>
											)}
											<span className={`${textHeading} font-medium`}>
												{normalizeCompanyName(device?.companyName) || 'Unknown'}
											</span>
										</div>
									<p className={`text-sm ${textBody}`}>
										#{index + 1} {device.os} - {device.browser}
									</p>
								</div>
								<span className={`text-xs ${textLabel}`}>
									{dayjs(device.lastCheckIn).fromNow()}
								</span>
							</div>
							{(() => {
								const parts = [
									device.city,
									device.region,
									device.country,
								].filter(v => v && v !== 'Unknown' && v !== '')
								return (
									<p className={`mt-1 text-xs ${textFaint}`}>
										📍 {parts.length ? parts.join(', ') : 'Unknown location'}
									</p>
								)
							})()}
							<div className={`mt-2 text-xs ${textLabel} space-y-1`}>
								<p>
									<span className="font-medium">First used:</span>{' '}
									{dayjs(device.firstUsed).format('MMM DD, YYYY [at] hh:mm A')}
								</p>
								{device.deviceType === 'extension' && (
									<p>
										<span className="font-medium">Extension:</span>{' '}
										{device?.extensionVersion || 'N/A'}
									</p>
								)}
							</div>
							</div>
						))}
					</div>
				</div>
			</div>
		)}

		{stripeLoading && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className={`${textHeading} text-xl`}>Loading Stripe data...</div>
			</div>
		)}

		{stripeModal && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
				<div className={`${modalBg} rounded-lg p-4 md:p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
					<div className="flex justify-between items-center mb-4">
						<div>
							<h2 className={`text-lg md:text-xl font-bold ${textHeading}`}>
								Stripe Status Check
							</h2>
							{stripeModal.dbStatus.stripeCustomerId && (
								<div className="flex items-center gap-2">
									<a
										href={`https://dashboard.stripe.com/customers/${stripeModal.dbStatus.stripeCustomerId}`}
										target="_blank"
										rel="noopener noreferrer"
										className={`${textBlue} text-sm hover:underline`}
									>
										Open in Stripe Dashboard →
									</a>
									{stripeModal.dbStatus.stripeCustomerFoundByEmail && (
										<span className={`${textYellow} text-xs`}>(found by email)</span>
									)}
								</div>
							)}
						</div>
						<button
							onClick={() => setStripeModal(null)}
							className={`${closeBtn} text-2xl`}
						>
							×
						</button>
					</div>

					{stripeModal.mismatch && (
						<div className={`rounded p-3 mb-4 ${isDarkMode ? 'bg-red-900/50 border border-red-500' : 'bg-red-50 border border-red-300'}`}>
							<p className={`${isDarkMode ? 'text-red-300' : 'text-red-700'} font-semibold`}>Mismatch Detected</p>
							{stripeModal.mismatchDetails?.map((detail, i) => (
								<p key={i} className={`${isDarkMode ? 'text-red-200' : 'text-red-600'} text-sm`}>{detail}</p>
							))}
						</div>
					)}

					{stripeModal.mismatchDetails && !stripeModal.mismatch && (
						<div className={`rounded p-3 mb-4 ${isDarkMode ? 'bg-yellow-900/50 border border-yellow-500' : 'bg-yellow-50 border border-yellow-300'}`}>
							{stripeModal.mismatchDetails.map((detail, i) => (
								<p key={i} className={`${isDarkMode ? 'text-yellow-200' : 'text-yellow-700'} text-sm`}>{detail}</p>
							))}
						</div>
					)}

					<div className="grid grid-cols-2 gap-4 mb-4">
						<div className={`${innerBg} p-3 rounded`}>
							<h3 className={`${textHeading} font-semibold mb-2`}>Database Status</h3>
							<p className={`text-sm ${textBody}`}>
								<span className={textLabel}>Email:</span> {stripeModal.dbStatus.email}
							</p>
							<p className={`text-sm ${textBody}`}>
								<span className={textLabel}>Plan:</span>{' '}
								<span className={stripeModal.dbStatus.subscriptionPaid ? textGreen : textLabel}>
									{stripeModal.dbStatus.subscriptionType}
								</span>
							</p>
							<p className={`text-sm ${textBody}`}>
								<span className={textLabel}>Paid:</span>{' '}
								<span className={stripeModal.dbStatus.subscriptionPaid ? textGreen : textRed}>
									{stripeModal.dbStatus.subscriptionPaid ? 'Yes' : 'No'}
								</span>
							</p>
							{stripeModal.dbStatus.trialEnd && (
								<p className={`text-sm ${textBody}`}>
									<span className={textLabel}>Trial End:</span>{' '}
									{dayjs(stripeModal.dbStatus.trialEnd).format('MMM DD, YYYY')}
								</p>
							)}
							{stripeModal.dbStatus.lastChecked && (
								<p className={`text-sm ${textBody}`}>
									<span className={textLabel}>Last Checked:</span>{' '}
									{dayjs(stripeModal.dbStatus.lastChecked).fromNow()}
								</p>
							)}
						</div>

						<div className={`${innerBg} p-3 rounded`}>
							<h3 className={`${textHeading} font-semibold mb-2`}>Stripe Status</h3>
							{stripeModal.stripeStatus ? (
								<>
									<p className={`text-sm ${textBody}`}>
										<span className={textLabel}>Active:</span>{' '}
										<span className={stripeModal.stripeStatus.hasActiveSubscription ? textGreen : textRed}>
											{stripeModal.stripeStatus.hasActiveSubscription ? 'Yes' : 'No'}
										</span>
									</p>
									<p className={`text-sm ${textBody}`}>
										<span className={textLabel}>Plan:</span>{' '}
										{stripeModal.stripeStatus.subscriptionType || 'None'}
									</p>
									<p className={`text-sm ${textBody}`}>
										<span className={textLabel}>Status:</span>{' '}
										<span className={
											stripeModal.stripeStatus.subscriptionStatus === 'active' ? textGreen :
											stripeModal.stripeStatus.subscriptionStatus === 'canceled' ? textRed :
											textYellow
										}>
											{stripeModal.stripeStatus.subscriptionStatus || 'N/A'}
										</span>
									</p>
									{stripeModal.stripeStatus.cancelAtPeriodEnd && (
										<p className={`text-sm ${textYellow}`}>
											Cancels at period end
										</p>
									)}
									{stripeModal.stripeStatus.currentPeriodEnd && (
										<p className={`text-sm ${textBody}`}>
											<span className={textLabel}>Period End:</span>{' '}
											{dayjs(stripeModal.stripeStatus.currentPeriodEnd).format('MMM DD, YYYY')}
										</p>
									)}
								</>
							) : (
								<p className={`${textLabel} text-sm`}>No Stripe customer ID</p>
							)}
						</div>
					</div>

					{stripeModal.payments.length > 0 && (
						<div className="mb-4">
							<h3 className={`${textHeading} font-semibold mb-2`}>
								Payment History ({stripeModal.payments.length})
							</h3>
							<div className="space-y-2">
								{stripeModal.payments.map((payment, i) => (
									<div key={i} className={`${innerBg} p-2 rounded flex justify-between items-center`}>
										<div>
											<span className={`text-sm font-medium ${payment.paid ? textGreen : textRed}`}>
												${payment.amount}
											</span>
											<span className={`${textLabel} text-xs ml-2`}>
												{payment.status}
											</span>
										</div>
										<div className="text-right">
											<p className={`${textBody} text-xs`}>
												{dayjs(payment.created).format('MMM DD, YYYY')}
											</p>
											{payment.hostedInvoiceUrl && (
												<a
													href={payment.hostedInvoiceUrl}
													target="_blank"
													rel="noopener noreferrer"
													className={`${textBlue} text-xs hover:underline`}
												>
													View Invoice
												</a>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{stripeModal.mismatch && (
						<button
							onClick={() => syncStripeStatus(stripeModal.userId)}
							className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded"
						>
							Sync with Stripe
						</button>
					)}
				</div>
			</div>
		)}

		{companiesModal && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
				<div className={`${modalBg} rounded-lg p-4 md:p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto`}>
					<div className="flex justify-between items-center mb-4">
						<h2 className={`text-lg md:text-xl font-bold ${textHeading}`}>
							{companiesModal.user} - Companies ({companiesModal.companies.length})
						</h2>
						<button
							onClick={() => setCompaniesModal(null)}
							className={`${closeBtn} text-2xl`}
						>
							×
						</button>
					</div>

					<div className="space-y-2">
						{companiesModal.companies.map((company, index) => (
							<div key={index} className={`${innerBg} p-2 rounded flex items-center`}>
								<span className={textHeading}>
									{normalizeCompanyName(company)}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		)}

		{trialModal && (
			<div
				className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
				onClick={() => setTrialModal(null)}
			>
				<div
					className={`${modalBg} rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto`}
					onClick={e => e.stopPropagation()}
				>
					<div className={`flex items-start justify-between gap-3 p-5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
						<div className="flex items-center gap-3">
							<div className={`flex items-center justify-center w-10 h-10 rounded-full ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
								<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
							</div>
							<div>
								<h2 className={`text-base font-semibold ${textHeading}`}>Extend Free Trial</h2>
								<p className={`text-xs ${textLabel}`}>Choose how long to extend access</p>
							</div>
						</div>
						<button
							onClick={() => setTrialModal(null)}
							className={`${closeBtn} text-2xl leading-none -mt-1`}
							aria-label="Close"
						>
							×
						</button>
					</div>

					<div className="p-5">
						<div className={`flex items-center justify-between rounded-xl p-2 ${innerBg}`}>
							<button
								onClick={() => setTrialDays(Math.max(1, trialDays - 1))}
								disabled={trialDays <= 1}
								className={`w-10 h-10 flex items-center justify-center rounded-lg text-xl font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'}`}
								aria-label="Decrease days"
							>
								−
							</button>
							<div className="flex flex-col items-center">
								<input
									type="number"
									min="1"
									value={trialDays}
									onChange={e => setTrialDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
									className={`w-20 bg-transparent border-0 text-center text-3xl font-bold ${textHeading} focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
								/>
								<span className={`text-xs ${textLabel} -mt-1`}>{trialDays === 1 ? 'day' : 'days'}</span>
							</div>
							<button
								onClick={() => setTrialDays(trialDays + 1)}
								className={`w-10 h-10 flex items-center justify-center rounded-lg text-xl font-bold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'}`}
								aria-label="Increase days"
							>
								+
							</button>
						</div>

						<div className="flex flex-wrap gap-2 mt-4">
							{[1, 7, 30, 365].map(d => (
								<button
									key={d}
									onClick={() => setTrialDays(d)}
									className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
										trialDays === d
											? 'bg-blue-600 text-white'
											: isDarkMode
												? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
									}`}
								>
									{d === 365 ? '1 year' : d === 30 ? '1 month' : `${d} days`}
								</button>
							))}
						</div>

						<div className={`flex items-center justify-between mt-4 rounded-xl px-4 py-3 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
							<span className={`text-xs ${textLabel}`}>New trial end</span>
							<span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
								{dayjs().add(trialDays, 'day').format('MMM DD, YYYY')}
							</span>
						</div>
					</div>

					<div className={`flex gap-2 p-5 pt-0`}>
						<button
							onClick={() => setTrialModal(null)}
							className={`flex-1 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
						>
							Cancel
						</button>
						<button
							onClick={() => updateFreeTrial(trialModal)}
							className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition-colors"
						>
							Extend Trial
						</button>
					</div>
				</div>
			</div>
		)}
		</>
	)
}

export default UsersList
